/**
 * InventoryTab — top-level dossier tab for the supplier inventory rollup.
 *
 * §5.BU §I.3 P3 Commit B (2026-06-19). Locked in
 *   docs/audits/2026-06-19_5bu_i3_p2_design.md §F + §G
 *
 * Mirror of frontend/src/components/performance/PerformanceTab.js:
 *   - Frontend gate (Layer 3 of the three-layer PROD-invisibility defense):
 *     when `inventoryUiEnabled` is FALSE, this component renders null and
 *     the parent excludes the tab from the strip entirely.
 *   - Lazy fetches `GET /api/suppliers/{supplierId}/inventory` on first
 *     mount (parent triggers mount via tab visit).
 *   - Renders six states: loading / resolving / error / disabled /
 *     no-data / success.
 *   - Composes the four sub-components per P2 §F.1.
 *
 * `supplierId` is resolved asynchronously by the parent (name -> id
 * lookup via /api/company/suppliers?search=...). While the parent's
 * resolution is in flight, `supplierId` is null and `resolutionComplete`
 * is false -- InventoryTab shows the loading spinner. If resolution
 * completes but fails (no matching supplier in this tenant's list),
 * `resolutionComplete` is true and `supplierId` is null -- InventoryTab
 * shows the no-data empty state (semantically: this supplier has no
 * inventory in this tenant, identical to 404 from the backend).
 *
 * Edge-case contract (P2 §H):
 *   - has_data === false  -> friendly empty state
 *   - flag === false      -> early-null (defense-in-depth)
 *   - backend 404         -> fall-through to empty state (mirror /scorecard
 *                            precedent at PerformanceTab.js:82-86)
 */
import React, { useEffect, useState } from 'react';
import {
  LoadingSpinner,
  ErrorMessage,
} from '../SharedComponents';
import InventoryKpiCardGrid from './InventoryKpiCardGrid';
import OverMaxMethodologyFooter from './OverMaxMethodologyFooter';
import WarehouseDrilldown from './WarehouseDrilldown';
import UncatalogedCoverageFooter from './UncatalogedCoverageFooter';
import { API_BASE } from '../../config';

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): read the `.sd`
// Linear token layer so this tab flips with the shell in the future dark lane.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

function InventoryTab({
  supplierId,
  token,
  inventoryUiEnabled,
  supplierName,
  resolutionComplete = true,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resolvedToNoSupplier = !supplierId && resolutionComplete;
  const skipFetch = !inventoryUiEnabled || !supplierId || !token;

  useEffect(() => {
    if (skipFetch) {
      if (resolvedToNoSupplier) {
        setLoading(false);
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(
      `${API_BASE}/api/suppliers/${encodeURIComponent(supplierId)}/inventory`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    )
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 404) {
            // Backend 404 -> fall-through to empty state (mirror
            // PerformanceTab pattern at PerformanceTab.js:82-86).
            setData({
              supplier_id: supplierId,
              inventory_ui_enabled: true,
              has_data: false,
              snapshot_date: null,
              snapshot_age_days: null,
              kpis: {},
              warehouses: [],
              uncataloged: null,
            });
            setLoading(false);
            return;
          }
          throw new Error(`Inventory request failed (${res.status})`);
        }
        const body = await res.json();
        if (cancelled) return;
        setData(body);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not load inventory');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supplierId, token, inventoryUiEnabled, skipFetch, resolvedToNoSupplier]);

  if (!inventoryUiEnabled) return null;

  if (loading) {
    return (
      <div data-testid="inventory-loading">
        <LoadingSpinner text="Loading inventory..." />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="inventory-error">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!data || data.has_data === false) {
    return (
      <div style={{ ...LINEAR_PANEL, padding: '16px' }}>
        <div
          data-testid="inventory-empty"
          style={{ padding: '40px 20px', textAlign: 'center' }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text)',
              marginBottom: '6px',
            }}
          >
            No inventory data yet
          </div>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-3)',
              maxWidth: '480px',
              margin: '0 auto',
            }}
          >
            {supplierName ? (
              <>
                Once an inventory snapshot is ingested that includes{' '}
                <strong>{supplierName}</strong>, KPI cards, warehouse
                breakdown, and coverage details will appear here.
              </>
            ) : (
              <>
                Once an inventory snapshot is ingested for this company,
                KPI cards, warehouse breakdown, and coverage details will
                appear here.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inventory-success">
      <InventoryKpiCardGrid kpis={data.kpis} />
      <OverMaxMethodologyFooter overMaxPosition={data.kpis?.over_max_position} />
      <WarehouseDrilldown warehouses={data.warehouses} collapsedByDefault />
      <UncatalogedCoverageFooter
        uncataloged={data.uncataloged}
        snapshotDate={data.snapshot_date}
        snapshotAgeDays={data.snapshot_age_days}
      />
    </div>
  );
}

export default InventoryTab;
